import { useCallback, useContext, useMemo, useState } from 'react'
import { MESSAGE_TEMPLATES_QUERY_KEY } from '../components/MessageTemplates'
import { MessageTemplateCtx } from '../providers/MessageTemplate.provider'
import type {
  DeliveryChannel,
  IMessageTemplate,
} from '../providers/MessageTemplate.provider'
import { IMessageTemplateSchema } from '../schemas/message-template.schema'
import type { ErrorToastHandler } from './useError'
import { useError } from './useError'
import { useQueryClientSingleton } from './useQueryClientSingleton'
import { useRouter } from 'next/router'
import {
  buildMessageTemplateDefinition,
  resolveMessageTemplateType,
} from '../constants/message-template'

export const useMessageTemplate = (props: {
  messageTemplates: IMessageTemplate[]
  currentChannel?: IMessageTemplate['channel']
  onErrorToast?: ErrorToastHandler
}) => {
  const { postMethod, channels, multiple } = useContext(MessageTemplateCtx)!

  const router = useRouter()
  const messageTemplateType = useMemo(
    () => resolveMessageTemplateType(router.query?.flow),
    [router.query?.flow],
  )

  const findMessageTemplates = useCallback(
    (channel: IMessageTemplate['channel']) => {
      if (props.currentChannel === 'email') {
        return props.messageTemplates?.filter(
          (messageTemplate) =>
            messageTemplate.channel === channel &&
            messageTemplate.type === messageTemplateType,
        )
      }

      return props.messageTemplates?.filter(
        (messageTemplate) => messageTemplate.channel === channel,
      )
    },
    [messageTemplateType, props.currentChannel, props.messageTemplates],
  )

  const [createMessageTemplate, setCreateMessageTemplate] =
    useState<DeliveryChannel>()

  const formId = 'create-message-template-form'

  const [isLoading, setIsLoading] = useState(false)

  const error = useError(props.onErrorToast)

  const queryClient = useQueryClientSingleton()

  const [tabIndex, setTabIndex] = useState(() => {
    if (props.currentChannel)
      return channels.findIndex((channel) => channel === props.currentChannel)

    return 0
  })

  const currentChannel = useMemo(() => channels[tabIndex], [channels, tabIndex])

  const onCreateMessageTemplate = useCallback(
    (
      messageTemplate: IMessageTemplateSchema,
      channel: DeliveryChannel,
      onSuccess: () => void,
    ) => {
      setIsLoading(true)

      postMethod({
        template: buildMessageTemplateDefinition(messageTemplate),
        channel: channel,
        type: messageTemplate.type
          ? resolveMessageTemplateType(messageTemplate.type)
          : messageTemplateType,
      })
        .then((res) => {
          queryClient.invalidateQueries({
            queryKey: [MESSAGE_TEMPLATES_QUERY_KEY],
          })

          onSuccess()

          setCreateMessageTemplate(undefined)
        })
        .catch(error.handleError)
        .finally(() => setIsLoading(false))
    },
    [error.handleError, messageTemplateType, postMethod, queryClient],
  )

  const currentMessageTemplates = useMemo(() => {
    return findMessageTemplates(currentChannel)
  }, [currentChannel, findMessageTemplates])

  return {
    currentChannel,
    currentMessageTemplates,
    setTabIndex,
    onCreateMessageTemplate,
    tabIndex,
    isLoading,
    channels,
    multiple,
    createMessageTemplate,
    setCreateMessageTemplate,
    formId,
  }
}
